import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withFetch,
} from '@angular/common/http';
import { ICON_SETTINGS } from '@progress/kendo-angular-icons';
import { AuthService } from '@auth0/auth0-angular';
import { MsalModule, MsalService } from '@azure/msal-angular';
import { of } from 'rxjs';
import { UpsCoreModule } from '@ups/xplat/web/core';
import { environment } from '@ups/xplat/core';
import { UserModule } from '@ups/user';
import {
  RYN_REPLAY_SIGN_IN,
  RYN_REPLAY_TOKEN_FACTORY,
  RYN_REPLAY_USER_RESOLVER,
  createReplayUserResolver,
  createRynReplayEntraSignIn,
  provideRynReplayReadSources,
} from '@ups/xplat/web/ryn-replay';
import { msalProviders } from './auth/msal.config';
import { appRoutes } from './app.routes';
import { createReplayMsalTokenFactory } from './replay-token.factory';
import { ReplayUserQueryClient } from './replay-user-query.client';

/**
 * Auth0's AuthService is `providedIn: 'root'`, so `inject(AuthService,
 * { optional: true })` still constructs it — and the factory then throws
 * NG0201 on missing `auth0.client` when AuthModule.forRoot was never
 * imported. This Entra-only app never signs in with Auth0; override the
 * root provider with a no-op stub so ups-header → ups-navbar (pulled in by
 * RynReplayPlayerRoutingModule) can construct.
 */
const entraOnlyAuth0Stub: Partial<AuthService> = {
  isAuthenticated$: of(false),
  isLoading$: of(false),
  user$: of(undefined),
  idTokenClaims$: of(undefined),
  loginWithRedirect: () => of(undefined),
  logout: () => undefined,
  getAccessTokenSilently: () => of('') as never,
};

/**
 * The whole provider list of the standalone bootstrap (WI 31715): main.ts calls
 * bootstrapApplication(App, appConfig), so there is no AppModule. The core,
 * user and MSAL NgModules contribute their providers through importProvidersFrom.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(UpsCoreModule, UserModule, MsalModule),
    // Zoneless change detection (Feature #31662): both app components are OnPush
    // and carry no template-read state, so CD is driven by signals, AsyncPipe,
    // template events and markForCheck() rather than zone.js. main.ts carries no
    // 'zone.js' import; do not re-add it there or to project.json polyfills.
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    { provide: ICON_SETTINGS, useValue: { type: 'font' } },
    { provide: AuthService, useValue: entraOnlyAuth0Stub },
    ...msalProviders,
    // Ryn Replay read half (#30072). The player and session list read the
    // gated read API via these sources; the token factory hands them the
    // viewer's Entra access token, or undefined when nobody is signed in (the
    // sources then render their unauthenticated state instead of firing a
    // request that would 401). No recorder is wired here on purpose: this app
    // only PLAYS sessions, it never captures them.
    {
      provide: RYN_REPLAY_TOKEN_FACTORY,
      useFactory: createReplayMsalTokenFactory,
      deps: [MsalService],
    },
    // Actionable Entra sign-in on the session list's wrong-issuer refusal
    // (#31381). This app is Entra-only, so MSAL is always present; the refusal
    // button starts a corporate sign-in via loginRedirect.
    {
      provide: RYN_REPLAY_SIGN_IN,
      useFactory: (msal: MsalService) =>
        createRynReplayEntraSignIn(msal, environment.entraId?.audience),
      deps: [MsalService],
    },
    {
      provide: RYN_REPLAY_USER_RESOLVER,
      useFactory: createReplayUserResolver,
      deps: [ReplayUserQueryClient],
    },
    ...provideRynReplayReadSources(),
  ],
};
